import { createFreePreviewMember } from 'actions/LuxPlusActions'
import AccountAccessModal, { AccountAccessModalResult } from 'components/Account/AccountAccess/AccountAccessModal'
import LayoutContainer from 'components/Common/LayoutContainer'
import { mediaQueryUp } from 'components/utils/breakpoint'
import Colour from 'components/utils/Colour'
import Group from 'components/utils/Group'
import Sheet from 'components/utils/Sheet'
import ModalContext from 'contexts/ModalContext'
import { useAppDispatch, useAppSelector } from 'hooks/reduxHooks'
import TextButton from 'LuxKit/Button/TextButton'
import Divider from 'LuxKit/Divider'
import SolidStarIcon from 'LuxKit/Icons/solid/SolidStarIcon'
import SolidTagAltIcon from 'LuxKit/Icons/solid/SolidTagAltIcon'
import SolidUnlockAltIcon from 'LuxKit/Icons/solid/SolidUnlockAltIcon'
import { setSvgIconSize } from 'LuxKit/SvgIcon'
import BodyText from 'LuxKit/Typography/BodyText'
import Heading from 'LuxKit/Typography/Heading'
import { LUXURY_PLUS } from 'luxPlus/constants/base'
import { getCanViewLuxPlusBenefits } from 'luxPlus/selectors/featureToggle'
import { rem } from 'polished'
import React, { Fragment, ReactElement, useCallback, useContext } from 'react'
import { selectLoggedIn } from 'selectors/accountSelectors'
import styled from 'styled-components'

interface BenefitItem {
  icon: ReactElement
  title: string
  description: string
}

const BENEFIT_ITEMS: Array<BenefitItem> = [
  {
    icon: <SolidTagAltIcon />,
    title: 'Member only pricing',
    description: 'Save hundreds on hotel offers, tours and local experiences',
  },
  {
    icon: <SolidStarIcon />,
    title: 'Bonus inclusions',
    description: 'Hotel credits, daily breakfast, room upgrades, on-board cruises credit and more',
  },
  {
    icon: <SolidUnlockAltIcon />,
    title: 'Exclusive offers',
    description: 'Unlock hidden offers and early access to our best offers - only for LuxPlus+ members',
  },
]

const BenefitsContainer = styled.div`
  display: grid;
  grid-template: auto 1px auto 1px auto / 1fr;
  gap: ${rem(16)};
  ${setSvgIconSize(24)};

  ${mediaQueryUp.tablet} {
    --item-w: minmax(auto, ${rem(310)});
    grid-template: auto / var(--item-w) 1px var(--item-w) 1px var(--item-w);
    gap: ${rem(32)};
    justify-content: center;
    ${setSvgIconSize(48)};
  }
`

const BenefitDivider = styled(Divider)`
  ${mediaQueryUp.tablet} {
    height: ${rem(126)};
  }
`

function LuxPlusMonthLandingPageBenefits() {
  const canViewLuxPlusBenefits = useAppSelector(getCanViewLuxPlusBenefits)
  const isLoggedIn = useAppSelector(selectLoggedIn)
  const showModal = useContext(ModalContext)
  const dispatch = useAppDispatch()
  const startFreePreview = useCallback(async() => {
    if (!isLoggedIn) {
      const loginResult = await showModal<AccountAccessModalResult>(<AccountAccessModal />)
      if (loginResult.loggedIn) {
        dispatch(createFreePreviewMember())
      }
    } else {
      dispatch(createFreePreviewMember())
    }
  }, [dispatch, isLoggedIn, showModal])

  if (canViewLuxPlusBenefits) return null

  return <Sheet padding="48 0">
    <LayoutContainer>
      <Group direction="vertical" gap={40}>
        <Heading variant="heading2" align="center">
          Year-round <i>benefits</i> with <Colour value="lux-plus">{LUXURY_PLUS.PROGRAM_NAME}</Colour>
        </Heading>
        <BenefitsContainer>
          {BENEFIT_ITEMS.map((benefitItem, index) => <Fragment key={index}>
            {index !== 0 && <BenefitDivider kind="primary" direction="horizontal" tabletDirection="vertical" />}
            <Group direction="vertical" horizontalAlign="center" gap={12}>
              {benefitItem.icon}
              <Group direction="vertical" gap={4}>
                <Heading variant="heading5" align="center">{benefitItem.title}</Heading>
                <BodyText variant="medium" align="center">{benefitItem.description}</BodyText>
              </Group>
            </Group>
          </Fragment>)}
        </BenefitsContainer>
        {!canViewLuxPlusBenefits && <Group direction="vertical" tabletHorizontalAlign="center">
          <TextButton kind="primary" size="large" shape="round" onClick={startFreePreview}>
            Start your FREE preview today
          </TextButton>
        </Group>}
      </Group>
    </LayoutContainer>
  </Sheet>
}

export default LuxPlusMonthLandingPageBenefits
